import type { FormSubmissionStatus } from '../utilities/form-status'
import { Availability } from '../domain/Availability'
import { NgClass, NgIf } from '@angular/common'
import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, output } from '@angular/core'
import {
    CountryISO,
    NgxIntlTelInputModule,
    PhoneNumberFormat,
    SearchCountryField,
} from 'ngx-intl-tel-input-gg'
import { CreateWaitlistEntryDTO } from '../services/DTOs/WaitlistEntryDTO'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { WaitlistService } from '../services/waitlist.service'
import { finalize } from 'rxjs'
import { CapitaliseOnBlurDirective } from '../directives/capitalise-on-blur.directive'

@Component({
    selector: 'app-waitlist-form',
    standalone: true,
    imports: [
        CapitaliseOnBlurDirective,
        NgClass,
        NgIf,
        NgxIntlTelInputModule,
        ReactiveFormsModule,
    ],
    templateUrl: './waitlist-form.component.html',
})
export class WaitlistFormComponent implements OnChanges {

    @Input() availability!: Availability
    @Input() requiresDogFriendly!: boolean | null
    @Input() requiresWheelchairAccess!: boolean | null
    @Input() requiresPramAccessible!: boolean | null
    @Input() requiresHighchairFriendly!: boolean | null
    @Input() partySize!: number
    @Input() eventId!: string | null
    @Input() reasonId!: string | null
    @Input() date!: Date | null
    @Input() idealTime!: Date | null
    @Input() organisationId!: string
    @Input() businessId!: string
    @Input() venueId!: string

    waitlistSubmitted = false
    waitlistSubmitting = false
    waitlistError: string | null = null

    submissionStatusChange = output<FormSubmissionStatus>()

    form: FormGroup = this.fb.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        emailAddress: ['', [Validators.required, Validators.email]],
        phoneNumber: [null, Validators.required],
    })

    phoneNumberCountryCode: CountryISO = CountryISO.UnitedKingdom
    SearchCountryField = SearchCountryField
    PhoneNumberFormat = PhoneNumberFormat

    constructor(
        private fb: FormBuilder,
        private waitlistService: WaitlistService,
        private changeDetectorRef: ChangeDetectorRef,
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['availability']) {
            this.phoneNumberCountryCode = this.availability.venue.address
                .getISO3166CountryCode().toLowerCase() as CountryISO
        }
    }

    submitWaitlistEntry() {
        if (this.form.invalid || !this.date) {
            this.submissionStatusChange.emit('validation_failed')
            this.form.markAllAsTouched()
            return
        }

        const values = this.form.value
        const dateString = this.formatDate(this.date)
        const timeString = this.formatTime(this.idealTime)

        const dto = new CreateWaitlistEntryDTO(
            values.firstName,
            values.lastName,
            values.emailAddress,
            values.phoneNumber?.e164Number ?? null,
            this.partySize,
            dateString,
            timeString,
            this.reasonId ?? null,
            this.eventId ?? null,
            this.requiresWheelchairAccess ?? false,
            this.requiresDogFriendly ?? false,
            this.requiresPramAccessible ?? false,
            this.requiresHighchairFriendly ?? false,
        )

        this.submissionStatusChange.emit('started')
        this.waitlistSubmitting = true
        this.waitlistError = null
        this.waitlistService.createWaitlistEntry(
            this.organisationId,
            this.businessId,
            this.venueId,
            dto,
        )
            .pipe(finalize(() => {
                this.waitlistSubmitting = false
                this.changeDetectorRef.detectChanges()
            }))
            .subscribe({
                next: () => {
                    this.waitlistSubmitted = true
                    this.submissionStatusChange.emit('succeeded')
                },
                error: () => {
                    this.submissionStatusChange.emit('failed')
                    this.waitlistError = 'Something went wrong. Please try again.'
                },
            })
    }

    private formatDate(date: Date): string {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    private formatTime(time: Date | null): string {
        if (!time) {
            return '19:00'
        }
        const hours = String(time.getHours()).padStart(2, '0')
        const minutes = String(time.getMinutes()).padStart(2, '0')
        return `${hours}:${minutes}`
    }
}
